import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Snackbar } from '@mui/material';
import inventoryItemService, { extractInventoryError } from '../../services/inventoryItemService';

/**
 * ITEM-ACTIVE.4 — instant flip, with a real way back.
 *
 * The chosen design drops the confirm dialog for the ordinary case, on the
 * grounds that the act is now genuinely reversible. That is only true if Undo
 * REALLY undoes it: this re-flips through the API and waits for the answer. An
 * Undo that merely hides the toast — or that repaints the row optimistically
 * and never calls anything — is a lie told at exactly the moment somebody has
 * realised they made a mistake.
 *
 * The window is deliberately short (6s) and there is no queue. A second flip
 * replaces the first offer rather than stacking, because an Undo that might
 * apply to either of two acts is worse than one that plainly applies to the
 * last.
 */

export const UNDO_WINDOW_MS = 6000;

export interface UndoableFlip {
  /** What just happened, for the toast: "Retired Barolo 2016". */
  message: string;
  /** Items to put back, and the state to put them back TO. */
  itemIds: string[];
  restoreTo: boolean;
}

interface Options {
  venueId: string | undefined;
  /** Re-read the list/item once an undo has actually landed. */
  onUndone: () => void | Promise<void>;
  onError: (message: string) => void;
}

export const useRetireUndo = ({ venueId, onUndone, onError }: Options) => {
  const [pending, setPending] = useState<UndoableFlip | null>(null);
  const [undoing, setUndoing] = useState(false);
  // A flip that lands while an undo is in flight must not resurrect the old
  // offer when the request returns.
  const seq = useRef(0);

  const offer = useCallback((flip: UndoableFlip) => {
    seq.current += 1;
    setPending(flip);
  }, []);

  const dismiss = useCallback(() => {
    seq.current += 1;
    setPending(null);
  }, []);

  const undo = useCallback(async () => {
    if (!pending || !venueId) return;
    const mine = seq.current;
    const { itemIds, restoreTo } = pending;
    setUndoing(true);
    try {
      // bulk-active covers one item as happily as forty, so the single and
      // batch paths undo through exactly the same call — one code path to be
      // right rather than two to keep in step.
      await inventoryItemService.bulkSetActive(venueId, itemIds, restoreTo);
      if (seq.current !== mine) return;
      setPending(null);
      await onUndone();
    } catch (err) {
      // Undo can genuinely fail — most plausibly because the name was taken
      // while the item sat retired. Say so; do not pretend it worked.
      onError(extractInventoryError(err));
      setPending(null);
    } finally {
      setUndoing(false);
    }
  }, [pending, venueId, onUndone, onError]);

  useEffect(() => {
    if (!pending) return undefined;
    const timer = setTimeout(() => setPending(null), UNDO_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  const toast = (
    <Snackbar
      open={Boolean(pending)}
      onClose={(_e, reason) => { if (reason !== 'clickaway') dismiss(); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      message={pending?.message ?? ''}
      action={(
        <Button
          size="small"
          onClick={undo}
          disabled={undoing}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {undoing ? 'Undoing…' : 'Undo'}
        </Button>
      )}
    />
  );

  return { offer, dismiss, toast, undoPending: Boolean(pending) };
};

export default useRetireUndo;
